import React, { Component } from "react";
import HoverOverlay from "../HoverOverlay";
import ImageInstruction from "views/components/Editor/elements/ImageInstruction/ImageInstruction";
import {
  generateSvgDomObject,
  getSelectedImageInstructionBorder,
  getFrameDimensions,
  calculateUpdatedVectorImageInstructions,
  validateImageInstructions
} from "./vectorUtils";
import { identity, pick, getPath, uuid } from "lib";
import { jsonStringEqual, sortedJsonStringEqual } from "lib/equalityUtils";
import { domParser as defaultParser } from "lib/defaultDomParser";
import { fetchSvgs } from "lib/svg/svg";
import { getPaletteFromSrc } from "lib/Colors/colorUtils";
import { isImportantPropsChanged } from "lib/elements";
import { getPlaceholderSize } from "views/components/Editor/elements/grid/imageInstructionUtils";
import { OutlineFilter } from "views/components/OutlineFilter/OutlineFilter";
import svgStyles from "./style.module.css";
import {
  memoizedGetGifImageSource as getGifImageSource,
  getGifFrameAtTime,
  preloadGifFrames
} from "lib/mediaSourceHelpers";
import { isGif } from "lib/isGif";
import { playVideo } from "lib/videoControlUtils";
import { getBrowserClientName } from "lib/getBrowserClientName";
import { BROWSER_NAMES, DEFAULT_VECTOR_MASK } from "lib/constants";
import "animate.css";
import {
  getCombinedAnimatedStyles,
  shouldAnimateElement
} from "lib/animationUtils";
import {
  calculateVideoTimeFromTimeline,
  getVideoPlaybackState,
  shouldSeekVideo
} from "lib/videoSyncUtils";

const importantElementProps = [
  "zoom",
  "style",
  "elementId",
  "elementData",
  "isSelected",
  "restrictions",
  "pageId",
  "groupId",
  "isDesignPartOfACollection",
  "isAllRestrictionsLocked",
  "isPhotoSelected",
  "restrictionsForDocument",
  "restrictionsForElement",
  "isPlaying",
  "isPaused",
  "currentPlayingTime",
  "isPreview",
  "imageInstruction",
  "mask",
  "scale"
];

export class Vector extends Component {
  static defaultProps = {
    connectDragSource: identity
  };

  constructor(props) {
    super(props);
    let svgDomElement = document.createElement("svg");

    if (
      !window.easil ||
      !window.easil.svgs ||
      !window.easil.svgs[props.elementData.src] ||
      !window.easil.svgs[props.elementData.src].data
    ) {
      const svgData = fetchSvgs([props.elementData.src]);
      svgData.pop().then(data => {
        this.setState({
          svgDomElement: (props.domParser || defaultParser)
            .parseFromString(data, "image/svg+xml")
            .querySelector("svg")
        });
      });
    } else {
      svgDomElement = (props.domParser || defaultParser)
        .parseFromString(
          window.easil.svgs[props.elementData.src].data,
          "image/svg+xml"
        )
        .querySelector("svg");
    }

    this.handleImageDrop = this.handleImageDrop.bind(this);
    this.handleImageDragHover = this.handleImageDragHover.bind(this);
    this.handleImageDragLeave = this.handleImageDragLeave.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.photoFrameHasImage = this.photoFrameHasImage.bind(this);
    this.handleDoubleClick = this.handleDoubleClick.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.renderSvgBorder = this.renderSvgBorder.bind(this);
    this.selectPhotoFrame = this.selectPhotoFrame.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.buildSvgRenderObject = this.buildSvgRenderObject.bind(this);
    this.buildImageInstructionList = this.buildImageInstructionList.bind(this);
    this.forceRedraw = this.forceRedraw.bind(this);
    this.handleDoubleClick = this.handleDoubleClick.bind(this);
    this.syncVectorToTimeline = this.syncVectorToTimeline.bind(this);

    this.wrapperRef = React.createRef();

    this.state = {
      isHovered: false,
      svgDomElement: svgDomElement,
      imageInstructionPreview: null,
      svgRenderObject: null,
      forceUpdateInterval: null,
      imageInstructionList: [],
      renderVersion: "",
      vectorPlaybackState: "visible", // 'hidden', 'visible'
      // Track GIF resume info: when resuming from pause, we need to show
      // extracted frames until the timeline catches up to avoid the GIF restarting
      gifResumeInfo: null // { resumeTime: number, pausedAtTime: number }
    };
  }

  componentDidMount() {
    this.buildSvgRenderObject();
    // Initialize vector state based on props
    if (this.props.isPlaying) {
      this.handleVectorPlayback();
    } else {
      this.setState({ vectorPlaybackState: "visible" });
    }

    // Preload GIF frames for faster scrubbing
    const { imageInstructions = [] } = this.props.elementData;
    imageInstructions.forEach(instruction => {
      if (instruction.previewSrc && isGif(instruction.previewSrc)) {
        preloadGifFrames(instruction.previewSrc);
      }
    });
  }

  componentWillUnmount() {
    if (this.playStartTimeout) {
      clearTimeout(this.playStartTimeout);
      this.playStartTimeout = null;
    }
    if (this.loopEndTimeout) {
      clearTimeout(this.loopEndTimeout);
      this.loopEndTimeout = null;
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      !sortedJsonStringEqual(nextState, this.state) ||
      isImportantPropsChanged(nextProps, this.props, importantElementProps)
    );
  }

  forceRedraw() {
    if (!this.wrapperRef || !this.wrapperRef.current) {
      return;
    }
    // a hacky solution to foreignObject rendering of video in firefox
    // DO NOT USE IN OTHER CASES
    if (this.wrapperRef.current.style["line-height"] === "1") {
      this.wrapperRef.current.style["line-height"] = "0";
    } else {
      this.wrapperRef.current.style["line-height"] = "1";
    }
  }

  hasAnimatedSources() {
    const { elementData } = this.props;
    if (!elementData.imageInstructions) return false;

    // Match the timeline logic: check for imageInstructions with duration
    return elementData.imageInstructions.some(
      instruction => instruction.duration
    );
  }

  /**
   * PHASE 2.5: Synchronize vector visibility and nested videos with timeline position
   * Replaces setTimeout-based delays with currentPlayingTime-based control
   *
   * UPDATED: Now properly handles videos in imageInstructions with their own timing properties
   */
  syncVectorToTimeline() {
    const { isPlaying, isPaused, currentPlayingTime, elementData } = this.props;
    const {
      delay = 0,
      duration,
      imageInstructions = [],
      isLooped = true
    } = elementData;
    const timelineTime = currentPlayingTime || 0;

    // Determine if vector should be visible based on timeline position
    const vectorStartTime = delay;

    // Calculate end time based on duration and loop state
    let vectorEndTime;
    if (duration !== undefined) {
      // If duration is explicitly set, use it
      vectorEndTime = delay + duration;
    } else if (!isLooped && imageInstructions.length === 1) {
      // For non-looping vectors with single image instruction, use instruction's duration
      const instruction = imageInstructions[0];
      const instructionDuration =
        instruction.duration || instruction.media?.duration;
      vectorEndTime = instructionDuration
        ? delay + instructionDuration
        : Infinity;
    } else {
      // For looping or multi-instruction vectors, show indefinitely
      vectorEndTime = Infinity;
    }

    const shouldBeVisible =
      timelineTime >= vectorStartTime && timelineTime < vectorEndTime;

    // When stopped (not playing and not paused), always show vector for editing
    const isStopped = !isPlaying && !isPaused;
    if (isStopped) {
      // Always show vector when stopped so it can be edited
      if (this.state.vectorPlaybackState !== "visible") {
        this.setState({ vectorPlaybackState: "visible" });
      }
    } else {
      // Only update visibility based on timeline position when playing or paused
      if (!shouldBeVisible && this.state.vectorPlaybackState !== "hidden") {
        // Hide vector when timeline is outside vector's time range
        this.setState({ vectorPlaybackState: "hidden" });
      } else if (
        shouldBeVisible &&
        this.state.vectorPlaybackState !== "visible"
      ) {
        // Show vector when timeline is within vector's time range
        this.setState({ vectorPlaybackState: "visible" });
      }
    }

    // Sync nested videos in vector imageInstructions to timeline position
    // Each video may have its own timing properties from its imageInstruction
    // Sync when: (1) playing/paused and within visible range, OR (2) stopped (for editing)
    const shouldSyncVideos = isStopped || shouldBeVisible;
    if (
      this.wrapperRef?.current &&
      shouldSyncVideos &&
      imageInstructions.length > 0
    ) {
      // Get all video elements in the vector
      const videos = [
        ...(this.wrapperRef.current.querySelectorAll("video") || [])
      ];

      if (videos.length === 0) return;

      // For each imageInstruction with a video source (has duration)
      imageInstructions.forEach(instruction => {
        if (!instruction.duration) {
          return; // Skip non-video instructions
        }

        // Determine if this video should loop
        // - Multiple image instructions: always loop (video stays visible for vector duration)
        // - Single image instruction: respect element's isLooped setting
        const shouldLoopVideo =
          imageInstructions.length > 1 ? true : isLooped !== false;

        // IMPORTANT: Even for looping videos, we need to sync when paused for scrubbing
        // Only skip sync for looping videos when actively playing (they handle looping automatically)
        if (shouldLoopVideo && !isPaused) {
          return; // Skip sync for looping videos during playback
        }

        // Try to find the corresponding video element using domId
        // Video elements are created with id pattern: `${instruction.domId}-${elementId}`
        const domId = instruction.domId;
        if (!domId) {
          return;
        }

        // Try to find the video by checking parent elements with the domId
        // Video ID format is `${domId}-${uniqueId}` so we need precise matching
        let videoElement = videos.find(video => {
          // First check if video itself has the domId
          // Use startsWith to avoid substring matches (e.g., "g1" matching "g10")
          if (video.id && video.id.startsWith(`${domId}-`)) {
            return true;
          }

          // Check if video is inside an element with the instruction's domId
          let parent = video.parentElement;
          while (parent && parent !== this.wrapperRef.current) {
            if (parent.id && parent.id.startsWith(`${domId}-`)) {
              return true;
            }
            parent = parent.parentElement;
          }
          return false;
        });

        // Fallback: If we have only one video and one instruction, just use it
        if (
          !videoElement &&
          videos.length === 1 &&
          imageInstructions.length === 1
        ) {
          videoElement = videos[0];
        }

        if (!videoElement || videoElement.readyState < 2) {
          return; // Skip if not found or not ready
        }

        // Calculate timing properties for the instruction
        // The instruction's delay is RELATIVE to when the vector becomes visible
        // So total delay = vector delay + instruction delay
        const vectorDelay = delay || 0;
        const relativeInstructionDelay = instruction.delay || 0;
        const totalDelay = vectorDelay + relativeInstructionDelay;

        const instructionStartOffset =
          instruction.startOffset || instruction.trimStart || 0;
        const instructionTrimDuration =
          instruction.trimDuration || instruction.duration;

        // Calculate the exact video time using the same logic as Video component
        const targetVideoTime = calculateVideoTimeFromTimeline(
          timelineTime,
          totalDelay,
          instructionStartOffset,
          instructionTrimDuration
        );

        // Non-looping videos: use instruction duration
        const effectiveDuration = instruction.duration;

        // Determine playback state
        const targetPlaybackState = getVideoPlaybackState(
          isPlaying,
          isPaused,
          timelineTime,
          totalDelay,
          effectiveDuration
        );

        // Only seek if difference is significant (throttle to 0.1s)
        if (shouldSeekVideo(videoElement.currentTime, targetVideoTime)) {
          videoElement.currentTime = targetVideoTime;
        }

        // Handle play/pause state
        if (targetPlaybackState === "playing" && videoElement.paused) {
          playVideo(videoElement);
        } else if (targetPlaybackState === "paused" && !videoElement.paused) {
          videoElement.pause();
        } else if (targetPlaybackState === "hidden") {
          // Video should be hidden (outside its time range)
          videoElement.pause();
          videoElement.currentTime = instructionStartOffset / 1000;
        }
      });
    }
  }

  handleVectorPlayback() {
    const { isPlaying, isPaused, elementData } = this.props;
    const { delay = 0, isLooped = true } = elementData;

    // Clear any existing timeouts
    if (this.playStartTimeout) {
      clearTimeout(this.playStartTimeout);
      this.playStartTimeout = null;
    }
    if (this.loopEndTimeout) {
      clearTimeout(this.loopEndTimeout);
      this.loopEndTimeout = null;
    }

    // Only apply delay logic if vector has animated sources
    if (!this.hasAnimatedSources()) {
      this.setState({ vectorPlaybackState: "visible" });
      return;
    }

    // If stopped (not playing and not paused), show vector for editing
    if (!isPlaying && !isPaused) {
      this.setState({ vectorPlaybackState: "visible" });
      return;
    }

    // If paused, let syncVectorToTimeline handle visibility based on timeline position
    if (!isPlaying && isPaused) {
      // Don't change visibility state - syncVectorToTimeline will handle it
      return;
    }

    // If there's a delay, start hidden
    if (delay > 0) {
      // Start hidden
      this.setState({ vectorPlaybackState: "hidden" });

      // Show after delay
      this.playStartTimeout = setTimeout(() => {
        this.setState({ vectorPlaybackState: "visible" });

        // For non-looping vectors with single imageInstruction, set up end timeout
        if (!isLooped && elementData.imageInstructions?.length === 1) {
          const instruction = elementData.imageInstructions[0];
          const duration = instruction.duration || instruction.media.duration;

          this.loopEndTimeout = setTimeout(() => {
            this.setState({ vectorPlaybackState: "hidden" });
          }, duration);
        }
      }, delay);
    } else {
      // No delay, show immediately
      this.setState({ vectorPlaybackState: "visible" });

      // For non-looping vectors with single imageInstruction, set up end timeout
      if (!isLooped && elementData.imageInstructions?.length === 1) {
        const instruction = elementData.imageInstructions[0];
        const duration = instruction.duration || instruction.media.duration;

        this.loopEndTimeout = setTimeout(() => {
          this.setState({ vectorPlaybackState: "hidden" });
        }, duration);
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !jsonStringEqual(prevProps.elementData, this.props.elementData) ||
      prevProps.zoom !== this.props.zoom ||
      !jsonStringEqual(
        prevState.imageInstructionPreview,
        this.state.imageInstructionPreview
      )
    ) {
      this.buildSvgRenderObject();
    }

    // PHASE 2.5: Timeline-based vector synchronization
    const timelineStateChanged =
      this.props.isPlaying !== prevProps.isPlaying ||
      this.props.isPaused !== prevProps.isPaused ||
      this.props.currentPlayingTime !== prevProps.currentPlayingTime;

    if (timelineStateChanged && this.hasAnimatedSources()) {
      this.syncVectorToTimeline();

      // Update GIF frames when:
      // 1. Scrubbing (paused with changing currentPlayingTime)
      // 2. Playing with gifResumeInfo (continuing from pause)
      const hasGifInstructions = this.props.elementData.imageInstructions?.some(
        instruction => instruction.previewSrc && isGif(instruction.previewSrc)
      );
      const shouldUpdateGifFrames =
        hasGifInstructions &&
        this.props.currentPlayingTime !== prevProps.currentPlayingTime &&
        (this.props.isPaused ||
          (this.props.isPlaying && this.state.gifResumeInfo !== null));

      if (shouldUpdateGifFrames) {
        this.buildImageInstructionList();
      }
    }

    // Handle vectorPlaybackState changes (e.g., when vector becomes visible after delay)
    if (prevState.vectorPlaybackState !== this.state.vectorPlaybackState) {
      // Rebuild image instruction list when vector becomes visible
      // This ensures GIFs are re-evaluated with correct playing state
      if (this.state.vectorPlaybackState === "visible") {
        this.buildImageInstructionList();
      }

      // Control video playback based on vectorPlaybackState
      const videos = [
        ...(this.wrapperRef.current?.querySelectorAll("video") || [])
      ];

      if (videos.length) {
        if (
          this.state.vectorPlaybackState === "visible" &&
          this.props.isPlaying
        ) {
          // Start playing videos when vector becomes visible
          videos.forEach(video => {
            playVideo(video);
          });
        } else if (this.state.vectorPlaybackState === "hidden") {
          // Pause videos when vector is hidden (during delay or after play)
          // But DON'T reset to 0 when paused - syncVectorToTimeline handles that
          if (!this.props.isPaused) {
            videos.forEach(video => {
              video.pause();
              video.currentTime = 0;
            });
          } else {
            // When paused, just pause but don't reset
            videos.forEach(video => {
              video.pause();
            });
          }
        }
      }
    }

    if (prevProps.isPlaying !== this.props.isPlaying) {
      // Track when resuming from pause so GIFs can continue from correct position
      const hasGifInstructions = this.props.elementData.imageInstructions?.some(
        instruction => instruction.previewSrc && isGif(instruction.previewSrc)
      );

      if (
        this.props.isPlaying &&
        !prevProps.isPlaying &&
        prevProps.isPaused &&
        hasGifInstructions
      ) {
        // Resuming from pause - record where we were so GIFs can catch up
        this.setState({
          gifResumeInfo: {
            resumeTime: Date.now(),
            pausedAtTime: prevProps.currentPlayingTime || 0
          }
        });
      } else if (!this.props.isPlaying) {
        // Stopping or pausing - clear resume info
        this.setState({ gifResumeInfo: null });
      }

      this.handleVectorPlayback();
      this.buildImageInstructionList();

      const videos = [
        ...(this.wrapperRef.current?.querySelectorAll("video") || [])
      ];

      const browserClientName = getBrowserClientName();

      if (videos.length) {
        if (this.props.isPlaying) {
          // Only start playing if vector is visible (not hidden by delay)
          if (this.state.vectorPlaybackState === "visible") {
            videos.forEach(video => {
              playVideo(video);
            });
          }

          // check if firefox hack fix is needed
          if (browserClientName === BROWSER_NAMES.FIREFOX) {
            // hacky fix for a firefox bug where video elements inside foreignObject elements
            // would not redraw while playing and so remained paused or jumped around
            const forceUpdateInterval = setInterval(this.forceRedraw, 0);
            this.setState({
              forceUpdateInterval
            });
          }
        } else {
          // IMPORTANT: Don't reset video to 0 when paused (only when stopped)
          // The new syncVectorToTimeline handles paused state
          if (!this.props.isPaused) {
            // Only reset to 0 when stopped (not paused)
            videos.forEach(video => {
              video.pause();
              video.currentTime = 0;
            });
          } else {
            // When paused, just pause but keep the current time
            // syncVectorToTimeline will handle seeking to the correct position
            videos.forEach(video => {
              video.pause();
            });
          }
        }
      }
      // check if firefox hack fix needs to be removed
      if (
        browserClientName === BROWSER_NAMES.FIREFOX &&
        this.state.forceUpdateInterval
      ) {
        clearInterval(this.state.forceUpdateInterval);
        this.setState({
          forceUpdateInterval: null
        });
      }
    }

    if (
      !jsonStringEqual(
        this.state.imageInstructionList,
        prevState.imageInstructionList
      )
    ) {
      this.buildSvgRenderObject();
    }

    if (
      !jsonStringEqual(
        this.props.imageInstructions,
        prevProps.imageInstructions
      )
    ) {
      this.buildImageInstructionList();
    }

    if (this.state.svgRenderObject !== prevState.svgRenderObject) {
      if (this.wrapperRef && this.wrapperRef.current) {
        const { imageInstructionList = [] } = this.state;
        imageInstructionList.forEach(instruction => {
          if (isGif(instruction.previewSrc)) {
            const imageDomId = `${instruction.domId}-${this.props.elementData.uniqueId}`;
            const svgImage = this.wrapperRef.current.querySelector(
              `*[id^="${imageDomId}"]`
            );

            svgImage.setAttribute("href", "");
            setTimeout(() => {
              svgImage.setAttribute("href", instruction.previewSrc);
            }, 0);
          }
        });
      }
    }
  }

  async buildImageInstructionList() {
    const { imageInstructionPreview, gifResumeInfo } = this.state;
    const { isPaused, currentPlayingTime, elementData, isPlaying } = this.props;
    const { delay = 0, isLooped = true } = elementData;

    let imageInstructionList = [...this.props.elementData.imageInstructions];

    // when we have a preview we need to replace its original and keep the others so there is none missing on render
    if (imageInstructionPreview) {
      const previewInstructionIndex = imageInstructionList
        .map(instruction => instruction.domId)
        .indexOf(imageInstructionPreview.domId);
      imageInstructionList[previewInstructionIndex] = imageInstructionPreview;
    }

    imageInstructionList = await Promise.all(
      imageInstructionList.map(async instruction => {
        if (isGif(instruction.previewSrc)) {
          const effectiveIsPlaying =
            this.state.vectorPlaybackState === "visible" ? isPlaying : false;

          // Use frame extraction when:
          // 1. Paused with a valid currentPlayingTime
          // 2. Playing but resumed from pause (gifResumeInfo exists) - to maintain continuity
          const shouldUseFrameExtraction =
            (isPaused && currentPlayingTime !== undefined) ||
            (effectiveIsPlaying && gifResumeInfo !== null);

          if (shouldUseFrameExtraction && currentPlayingTime !== undefined) {
            const instructionDelay = instruction.delay || 0;
            const totalDelay = delay + instructionDelay;
            // Calculate the time within the GIF based on timeline position
            const gifTime = Math.max(0, currentPlayingTime - totalDelay);
            const instructionSrc = await getGifFrameAtTime(
              instruction.previewSrc,
              gifTime,
              isLooped // Use vector's loop state
            );
            return {
              ...instruction,
              previewSrc: instructionSrc
            };
          }

          const instructionSrc = await getGifImageSource({
            isPlaying: effectiveIsPlaying,
            gifSource: instruction.previewSrc,
            isLooped: isLooped // Use vector's loop state
          });
          return {
            ...instruction,
            previewSrc: instructionSrc
          };
        }
        return instruction;
      })
    );

    this.setState({
      imageInstructionList
    });

    return imageInstructionList;
  }

  buildSvgRenderObject() {
    const { autoPlay } = this.props;
    const {
      fillColors,
      srcWidth,
      srcHeight,
      scale,
      width,
      height,
      resizableX,
      resizableY
    } = this.props.elementData;

    this.buildImageInstructionList();

    const { zoom } = this.props;
    const { imageInstructionList } = this.state;

    const svgRenderObject = generateSvgDomObject({
      svgDomElement: this.state.svgDomElement.cloneNode(true),
      fillColors,
      width,
      height,
      srcWidth,
      srcHeight,
      scale,
      resizableX,
      resizableY,
      imageInstructions: imageInstructionList,
      zoom,
      element: this.props.elementData,
      elementId: `${this.props.elementData.uniqueId}${
        this.props.isPreview ? "-preview" : ""
      }`,
      autoPlay
    });

    this.setState({
      svgRenderObject,
      renderVersion: uuid()
    });

    // need to force an update here since state checks don't see changes
    // in element objects in state
    this.forceUpdate();

    return svgRenderObject;
  }

  onSelect(e) {
    e.stopPropagation();
    e.preventDefault();

    /* if user pressed shift or meta/command(mac), we append the to the selection */
    const append = e.shiftKey || e.metaKey;

    // Guard against missing onSelectItem prop (e.g., when used in crop preview)
    if (this.props.onSelectItem) {
      this.props.onSelectItem({ append });
    }
  }

  async handleImageDragHover({ domId, imageInstruction }) {
    const { imageInstructions } = this.props.elementData;
    const { imageInstructionPreview } = this.state;

    // initial check to see if imageInstructions contain essesntial properties
    // prevent setting of preview when asset image hasn't been built
    const areImageInstructionsValid = validateImageInstructions(
      imageInstruction
    );

    if (!areImageInstructionsValid) {
      this.setState({
        imageInstructionPreview: null
      });
      return;
    }

    // do not try to adjust preview when nothing has changed
    if (
      imageInstructionPreview &&
      imageInstructionPreview.dragId === imageInstruction.dragId
    ) {
      return;
    }

    const svgImageTag = this.state.svgDomElement.querySelector(
      `#${domId} > image`
    );

    const maskSize = {
      width: svgImageTag.getAttribute("width"),
      height: svgImageTag.getAttribute("height")
    };

    // ensure we get the actual original and not just the first one
    const originalImageInstructionIndex = imageInstructions
      .map(instruction => instruction.domId)
      .indexOf(domId);
    const originalImageInstruction =
      imageInstructions[originalImageInstructionIndex];

    const imageInstructionPreviewUpdated = await calculateUpdatedVectorImageInstructions(
      {
        imageInstruction,
        originalImageInstruction,
        maskSize
      }
    );

    this.setState({
      imageInstructionPreview: {
        ...imageInstructionPreviewUpdated,
        filter: originalImageInstruction.filter,
        filters: originalImageInstruction.filters,
        opacity: originalImageInstruction.opacity
      }
    });
  }

  handleImageDragLeave() {
    if (!this.state.imageInstructionPreview) return;

    this.setState({
      imageInstructionPreview: null
    });
  }

  stopPropagationAndPreventDefault(e) {
    e.stopPropagation();
    e.preventDefault();
  }

  async handleImageDrop({
    assetElement,
    domId,
    imageInstruction: dropImageInstruction
  }) {
    const {
      imageInstruction,
      addActionToPropagationQueue,
      elementData
    } = this.props;
    const { imageInstructionPreview } = this.state;

    if (!imageInstructionPreview) {
      this.setState({
        imageInstructionPreview: null
      });
      return;
    }

    const svgImageTag = this.state.svgDomElement.querySelector(
      `#${domId} > image`
    );

    const maskSize = {
      width: svgImageTag.getAttribute("width"),
      height: svgImageTag.getAttribute("height")
    };

    addActionToPropagationQueue({
      assetElement,
      uniqueId: elementData.uniqueId,
      domId,
      actionType: "vectorImageReplace",
      imageInstruction: dropImageInstruction,
      maskSize,
      originalElement: {
        ...pick(elementData, ["value", "src", "id"]),
        imageInstructions: (
          elementData.imageInstructions || []
        ).map(instruction => pick(instruction, ["src", "domId"])),
        groupId: this.props.groupId,
        pageId: this.props.pageId,
        domId
      }
    });

    this.setState({
      imageInstructionPreview: null
    });

    // PERFORMANCE OPTIMIZATION: Update shape mask immediately with empty palette
    // Palette will be applied asynchronously after insertion
    const imageInstructionUpdated = {
      scaleX: 1,
      scaleY: 1,
      ...imageInstruction,
      ...imageInstructionPreview,
      palette: [] /* the palette will be generated async */
    };

    this.props.updateGridImageInstructions({
      elementId: this.props.elementId,
      imageInstructions: [imageInstructionUpdated]
    });

    // Extract palette in background and update element after insertion
    getPaletteFromSrc(imageInstructionPreview.previewUrl)
      .then(palette => {
        /* reason we check the domId here is because the user could have replaced
         * the image by the time the palette has been fetched. Also check if the
         * element still exists in the design */
        const currentInstruction =
          this.props.elementData &&
          this.props.elementData.imageInstructions &&
          this.props.elementData.imageInstructions.find(
            inst => inst.domId === domId
          );

        if (
          currentInstruction &&
          currentInstruction.id === imageInstructionUpdated.id
        ) {
          this.props.updateGridImageInstructions({
            elementId: this.props.elementId,
            imageInstructions: [
              {
                ...currentInstruction,
                palette
              }
            ]
          });
        }
      })
      .catch(error => {
        // Silently handle palette extraction errors - element is already inserted
        console.warn("Failed to extract palette for shape mask:", error);
      });
  }

  photoFrameHasImage(domId) {
    const { elementData } = this.props;
    const imageInstructions = elementData.imageInstructions.filter(
      imageInstruction => imageInstruction.domId === domId
    );

    const matchingImageInstructionFound =
      Boolean(imageInstructions) && Boolean(imageInstructions[0]);
    if (matchingImageInstructionFound) {
      return Boolean(
        imageInstructions[0].id &&
          (imageInstructions[0].mediaId || imageInstructions[0].src)
      );
    }
    return false;
  }

  selectPhotoFrame(_event, domId) {
    const { elementData } = this.props;

    if (!elementData.imageInstructions) {
      return;
    }

    this.props.selectPhotoInFrame({ domId });
  }

  handleClick = (event, domId) => {
    this.stopPropagationAndPreventDefault(event);

    if (this.props.isSelected) {
      this.selectPhotoFrame(event, domId);
      return;
    }
    this.onSelect(event);
  };

  handleDoubleClick = (_event, domId) => {
    if (this.props.restrictions.includes("cropping")) {
      return;
    }
    if (this.photoFrameHasImage(domId)) {
      this.props.startPhotoFrameCropMode();
    } else {
      const { resizableX, resizableY } = this.props.elementData || {};
      // do not enter crop mode for a vector that is resizable
      const isResizableVector = [resizableX, resizableY].some(x => !!x);
      if (!isResizableVector) {
        this.props.startVectorCropMode();
      }
    }
  };

  renderSvgBorder(_svg, selectedDomId) {
    const svgCloned = _svg.cloneNode(true);

    const svg = getSelectedImageInstructionBorder(svgCloned, selectedDomId);
    const mask = this.props.elementData.mask || DEFAULT_VECTOR_MASK;
    const leftMaskOffset = (-mask.left || 0) * this.props.elementData.scale;
    const topMaskOffset = (-mask.top || 0) * this.props.elementData.scale;

    return (
      <div
        style={{
          position: "absolute",
          top: `${topMaskOffset}px`,
          left: `${leftMaskOffset}px`,
          width: this.props.elementData.width,
          height: this.props.elementData.height,
          pointerEvents: "none",
          zIndex: -1
        }}
        dangerouslySetInnerHTML={{ __html: svg.outerHTML }}
      />
    );
  }

  handleMouseEnter() {
    if (this.state.isHovered) return;
    this.setState({
      isHovered: true
    });
  }

  handleMouseLeave() {
    this.setState({
      isHovered: false
    });
  }

  render() {
    const {
      scale,
      width,
      height,
      opacity,
      instructionScale,
      resizableX,
      resizableY
    } = this.props.elementData;

    const {
      restrictionsForDocument,
      restrictionsForElement,
      isSelected,
      zoom,
      isAllRestrictionsLocked,
      audioPlayableElementsForPage = [],
      isCropMask
    } = this.props;

    const { vectorPlaybackState } = this.state;

    // Control playback of child components based on vector playback state
    // If vector is hidden due to delay, child components should not be playing
    const effectiveIsPlaying =
      vectorPlaybackState === "visible" ? this.props.isPlaying : false;

    let svg = this.state.svgRenderObject;

    if (!svg) {
      return null;
    }

    const isResizableVector = [resizableX, resizableY].some(x => !!x);

    let style = {
      width,
      height,
      lineHeight: 0,
      opacity: opacity,
      overflow: "visible",
      visibility: vectorPlaybackState === "hidden" ? "hidden" : "visible"
    };

    const finalStyle = {
      ...this.props.style,
      ...style
    };

    if (isCropMask) {
      finalStyle.pointerEvents = "none";
    }

    const isHovered = this.state.isHovered;

    const element = {
      ...this.props.elementData,
      svg,
      type: "vector"
    };

    const isOutlined =
      !!element.outline &&
      (!!element.outline.width || !!element.outline.fade) &&
      !(element.outline.width === "0" && element.outline.fade === "0");

    // Turns out this.props.isPhotoSelected is really the domId of the selected imageInstruction
    // renaming here to make code in this component easier to understand
    const selectedDomId = this.props.isPhotoSelected;
    const isPhotoSelected = Boolean(this.props.isPhotoSelected);

    const mask = this.props.elementData.mask || DEFAULT_VECTOR_MASK;

    // define default masking variables
    let unmaskedVector = this.props.elementData,
      leftMaskOffset = 0,
      topMaskOffset = 0,
      maskedVectorStyles = {};

    // conditional assignments to masking variables
    if (!isResizableVector) {
      unmaskedVector = {
        ...this.props.elementData,
        top:
          this.props.elementData.top -
          this.props.elementData.mask.top * this.props.elementData.scale,
        left:
          this.props.elementData.left -
          this.props.elementData.mask.left * this.props.elementData.scale,
        width:
          this.props.elementData.srcWidth * this.props.elementData.scale * zoom,
        height:
          this.props.elementData.srcHeight *
          this.props.elementData.scale *
          zoom,
        mask: DEFAULT_VECTOR_MASK,
        srcWidth: this.props.elementData.srcWidth * zoom,
        srcHeight: this.props.elementData.srcHeight * zoom,
        instructionScale: this.props.elementData.scale * zoom
      };

      leftMaskOffset = (-mask.left || 0) * scale;
      topMaskOffset = (-mask.top || 0) * scale;

      maskedVectorStyles = {
        height: `${this.props.elementData.srcHeight *
          this.props.elementData.scale}px`,
        width: `${this.props.elementData.srcWidth *
          this.props.elementData.scale}px`,
        transform: `translate(${-(mask.left * scale)}px, ${-(
          mask.top * scale
        )}px)`
      };
    }

    const Vector = (
      <div style={finalStyle} ref={this.wrapperRef}>
        {isOutlined && !isCropMask && (
          <>
            <OutlineFilter
              elementData={element}
              svg={svg}
              zoom={zoom}
              imageInstructions={this.state.imageInstructionList}
              scale={scale}
              instructionScale={instructionScale}
              leftMaskOffset={leftMaskOffset}
              topMaskOffset={topMaskOffset}
              isModifying={this.props.isModifying}
            />
          </>
        )}
        <div
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
          onClick={isCropMask ? undefined : this.onSelect}
          style={{
            overflow: "hidden",
            width: `${this.props.elementData.width}px`,
            height: `${this.props.elementData.height}px`,
            position: "absolute"
          }}
        >
          <div
            ref={this.setElementRef}
            dangerouslySetInnerHTML={{ __html: svg.outerHTML }}
            className={svgStyles.originSvgWrapper}
            onDoubleClick={isCropMask ? undefined : this.handleDoubleClick}
            style={{
              opacity: getPath(
                this.props.elementData,
                "outline.knockout",
                false
              )
                ? 0
                : 1,
              ...maskedVectorStyles
            }}
          />

          {isPhotoSelected && this.renderSvgBorder(svg, selectedDomId)}
          {this.state.imageInstructionList.map(imageInstruction => {
            // Use imageInstruction.isMuted if available, otherwise default to false (unmuted)
            // Fall back to audioPlayableElementsForPage check only if it exists and has entries
            let isMuted = false; // Default: unmuted
            if (imageInstruction.isMuted !== undefined) {
              // Use explicit isMuted value if set
              isMuted = imageInstruction.isMuted;
            } else if (
              audioPlayableElementsForPage &&
              audioPlayableElementsForPage.length > 0
            ) {
              // Fall back to audioPlayableElementsForPage check (legacy behavior)
              isMuted = !audioPlayableElementsForPage.find(
                audioPlayableElement =>
                  audioPlayableElement.elementId ===
                    this.props.elementData.uniqueId &&
                  audioPlayableElement.instructionId ===
                    imageInstruction.uniqueId
              );
            }
            // Otherwise remains false (unmuted)

            const placeholder = getPlaceholderSize(imageInstruction, element);
            const frameDimensions = getFrameDimensions({
              vectorId: this.props.elementId,
              frameId: imageInstruction.domId,
              vectorElement: isCropMask
                ? this.props.elementData
                : unmaskedVector,
              zoom
            });
            if (!frameDimensions) return null;
            return (
              <ImageInstruction
                imageDoesExist={imageInstruction.type === "image"}
                restrictionsForDocument={restrictionsForDocument}
                restrictionsForElement={restrictionsForElement}
                domId={imageInstruction.domId}
                height={frameDimensions.height / zoom}
                key={imageInstruction.domId}
                left={placeholder.left * scale}
                onClick={this.handleClick}
                onDoubleClick={this.handleDoubleClick}
                onImageDragHover={this.handleImageDragHover}
                onImageDragLeave={this.handleImageDragLeave}
                onImageDrop={this.handleImageDrop}
                scale={instructionScale || scale}
                instructionScale={instructionScale}
                top={placeholder.top * scale}
                width={frameDimensions.width / zoom}
                zoom={zoom}
                imageInstruction={imageInstruction}
                element={this.props.elementData}
                isPlaying={effectiveIsPlaying}
                isPaused={this.props.isPaused}
                autoPlay={this.props.autoPlay}
                isContentHidden={getPath(
                  this.props.elementData,
                  "outline.knockout",
                  false
                )}
                isMuted={isMuted}
                leftMaskOffset={leftMaskOffset}
                topMaskOffset={topMaskOffset}
                renderVersion={this.state.renderVersion}
              />
            );
          })}
          {isHovered && !isAllRestrictionsLocked && !isSelected && (
            <HoverOverlay height={height} width={width} zoom={zoom} />
          )}
        </div>
      </div>
    );

    const shouldAnimate = shouldAnimateElement(
      this.props.elementData,
      this.props.isPlaying,
      this.props.isPaused || false
    );
    if (shouldAnimate) {
      const animationWrapperStyle = getCombinedAnimatedStyles(
        { position: "relative" },
        this.props.elementData,
        this.props.isPlaying,
        this.props.pageId,
        this.props.zoom,
        this.props.currentPlayingTime || 0,
        this.props.isPaused || false
      );

      return this.props.connectDragSource(
        <div style={animationWrapperStyle}>{Vector}</div>
      );
    }

    return this.props.connectDragSource(Vector);
  }
}

export default Vector;
